/**
 * The one row shell every notice in the employee bell renders through —
 * leave decisions (`employee_notification_bell.tsx`) and on-device notices
 * (`employee_device_notice_row.tsx`). One owner for the tap target, the
 * icon/title/time layout and the muted body, so the two kinds cannot drift.
 */

export function BellRow({
  icon,
  title,
  time,
  children,
  onOpen,
  testId,
}: {
  icon: React.ReactNode;
  title: string;
  time: string;
  /** The muted body, plus anything the notice adds under it. */
  children: React.ReactNode;
  onOpen: () => void;
  testId: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-start gap-2.5 text-start px-3 py-2.5 hover:bg-[var(--color-menu-hover)]"
      data-testid={testId}
    >
      {icon}
      <span className="min-w-0 flex-1">
        {/* Title and time share a row: "what" and "when" are the two things
            read at a glance, and stacking them would push the sentence below
            the fold of a 340px panel. */}
        <span className="flex items-baseline justify-between gap-2">
          <span className="font-semibold text-[13px]">{title}</span>
          <span className="shrink-0 text-[11px] text-[color:var(--ink-muted)]">
            {time}
          </span>
        </span>
        {children}
      </span>
    </button>
  );
}

/** The round icon badge at the start of a bell row. */
export function BellRowIcon({
  className,
  children,
}: {
  /** Background + text colour classes for the circle. */
  className: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <span
      className={`shrink-0 inline-flex items-center justify-center w-6 h-6 mt-0.5 rounded-full ${className}`}
      aria-hidden="true"
    >
      {children}
    </span>
  );
}
